import React, { useMemo, useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import { Icons } from '../icons/Icons';
import CableTessera from './CableTessera';
import { exportSchematicToPDFColor, exportSchematicNestedToPDF } from '../../utils/export';
import HybridCableVisualization from './HybridCableVisualization';
import { createSortContext, universalSort } from '../../utils/sortingLogic';
import { getDisplayTitle, getCableDisplayName, analyzeAndGroupCables } from '../../utils/cableLogic';
import { useGroupedCables } from '../../hooks/useGroupedCables';
import { getGroupDisplayTitle } from '../../utils/groups';
import { buildNestedCableStructure } from '../../utils/nestedCableStructure';
import SchematicNestedVisualization from './SchematicNestedVisualization';
import {
    PDF_CONSTANTS,
    getScale,
    estimateLogicalSubGroupHeight,
    estimateGroupHeightFromLogicalSubGroups,
    calculateGroupHeightFromDOM,
    estimateSheathChunkHeight
} from '../../utils/pdfHeightEstimation';

// Componente visualizzazione schematica
const SchematicCableView = ({
    cables,
    settings,
    setListSortMode,
    hoveredCable,
    setHoveredCable,
    isMobile,
    // Funzioni per la gestione dei cavi
    editCable,
    editMultipolarCable,
    deleteCable,
    deleteMultipolarCable,
    validateCable,
    editingIndex,
    editingMultipolarIndex,
    showDeleteButtons,
    toggleDeleteButton,
    // Props per la selezione dei cavi
    isSelectionModeEnabled,
    selectedCables,
    // Guaine
    availableSheaths = [],
    // Opzione anteprima (nasconde navbar filtri)
    isPreviewMode = false,
    previewTitle = null
}) => {
    const { t, language } = useI18n();
    const sortOptions = [
        { value: 'numerazione', label: t('schematic.numbering') },
        { value: 'siglatura', label: t('schematic.signature') },
        { value: 'colore', label: t('schematic.color') },
        { value: 'sezione', label: t('schematic.section') },
        { value: 'coloreSezione', label: t('schematic.colorSection') },
        { value: 'gruppo', label: t('schematic.groupSort') }
    ];

    const [localHoveredCable, setLocalHoveredCable] = useState(null);
    const [dynamicGroupsDivision, setDynamicGroupsDivision] = useState(false);
    const [debugMode, setDebugMode] = useState(false);
    const [useNestedSchematic, setUseNestedSchematic] = useState(false);
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

    // Filtra i cavi in base alla selezione per il raggruppamento
    const cablesForGrouping = isSelectionModeEnabled && selectedCables.size > 0
        ? cables.filter((_, index) => selectedCables.has(index))
        : cables;


    // Usa il hook per il raggruppamento dei cavi
    const { groupCablesByMode } = useGroupedCables(cablesForGrouping, settings.listSortMode, settings);
    const originalGroupedCables = useMemo(() => groupCablesByMode(), [groupCablesByMode]);
    const nestedBaseGroups = useMemo(() => {
        if (dynamicGroupsDivision) {
            return divideGroupsIntoSubgroups(originalGroupedCables);
        }
        return originalGroupedCables;
    }, [originalGroupedCables, dynamicGroupsDivision, settings]);

    const nestedSchematicNodes = useMemo(() => {
        const forcedGroups = [];
        const normalCables = [];

        Object.entries(nestedBaseGroups || {}).forEach(([key, groupData]) => {
            if (groupData?.type === 'forced_group') {
                forcedGroups.push({
                    isForcedGroup: true,
                    key,
                    groupData
                });
            } else if (Array.isArray(groupData)) {
                normalCables.push(...groupData);
            }
        });

        return buildNestedCableStructure({
            settings,
            forcedGroups,
            normalCables,
            dynamicGroupsDivision
        });
    }, [nestedBaseGroups, settings, dynamicGroupsDivision]);

    const renderLegacyGroup = ([siglatura, groupData]) => {
        if (groupData.type === 'forced_group') {
            const sortedCables = groupData.allCables;
            const subGroups = groupData.subGroups;
            const nomeGruppo = groupData.groupName;

            return (
                <div
                    key={siglatura}
                    id={`cable-group-${siglatura}`}
                    className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
                >
                    <div className="mb-6">
                        <div className="flex items-center justify-between">
                            <div className="flex-1 mr-4">
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        {getGroupDisplayTitleWithSubgroup(groupData, settings)}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {sortedCables.length === 1
                                            ? `1 ${t('schematic.cable')}`
                                            : `${sortedCables.length} ${t('schematic.cables')}`}
                                        {subGroups && subGroups.length > 1 ? ` ${t('schematic.cablesInSubgroups', { count: subGroups.length })}` : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {t('schematic.groupLabel')} {nomeGruppo}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6 bg-gray-50 rounded-lg p-4">
                        <HybridCableVisualization
                            cables={sortedCables}
                            settings={settings}
                            siglatura={siglatura}
                            hoveredCable={localHoveredCable}
                            setHoveredCable={setLocalHoveredCable}
                            isMobile={isMobile}
                            availableSheaths={availableSheaths}
                        />
                    </div>

                    {!isMobile && (
                        <div className="bg-blue-50 rounded-lg p-6 h-full flex flex-col">
                            <div className="space-y-2">
                                {sortedCables.map((cable, idx) => {
                                    const isHovered = localHoveredCable === cable.originalIndex;
                                    const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);

                                    return (
                                        <div
                                            key={`${cable.originalIndex}-${idx}`}
                                            className="transition-all duration-200"
                                            onMouseEnter={() => setLocalHoveredCable(cable.originalIndex)}
                                            onMouseLeave={() => setLocalHoveredCable(null)}
                                            style={isHovered ? {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 8px rgba(59, 130, 246, 0.3)'
                                            } : {}}
                                        >
                                            <CableTessera
                                                cable={cable}
                                                displaySiglatura={displaySiglatura}
                                                onCableClick={null}
                                                onEditCable={editCable}
                                                onEditMultipolarCable={editMultipolarCable}
                                                onDeleteCable={deleteCable}
                                                onDeleteMultipolarCable={deleteMultipolarCable}
                                                showSiglatura={true}
                                                clickable={false}
                                                settings={settings}
                                                validateCable={validateCable}
                                                isHovered={isHovered}
                                                editingIndex={editingIndex}
                                                editingMultipolarIndex={editingMultipolarIndex}
                                                showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
                                                onToggleDelete={() => toggleDeleteButton(cable.originalIndex)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        const sortedCables = Array.isArray(groupData) ? groupData : [];
        const subGroups = analyzeAndGroupCables(sortedCables, settings);
        const hasMultipleSubGroups = subGroups.length > 1;
        const hasMixedTypes = subGroups.some(g => g.isSingle) && subGroups.some(g => !g.isSingle);

        return (
            <div
                key={siglatura}
                id={`cable-group-${siglatura}`}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    {getDisplayTitleWithSubgroup(siglatura, sortedCables, false, null, settings)}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {(() => {
                                        const isSheathGroup = siglatura && siglatura.startsWith('sheath_');

                                        if (sortedCables.length === 1) {
                                            return `1 ${t('schematic.cable')}`;
                                        } else if (isSheathGroup) {
                                            return `${sortedCables.length} ${t('schematic.cables')}`;
                                        } else {
                                            return `${sortedCables.length} ${t('schematic.cables')}${hasMultipleSubGroups ? ` ${t('schematic.cableSegmentsInGroups', { count: subGroups.length })}` : ''}`;
                                        }
                                    })()}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2 flex-shrink-0">
                            {hasMixedTypes && (
                                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {t('schematic.hybrid')}
                                </div>
                            )}
                            {sortedCables.length > 1 && !hasMixedTypes && (() => {
                                const isSheathGroup = siglatura && siglatura.startsWith('sheath_');
                                return isSheathGroup ? (
                                    <div className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {t('schematic.sheath')}
                                    </div>
                                ) : (
                                    <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {t('schematic.multiple')}
                                    </div>
                                );
                            })()}
                            {sortedCables.length === 1 && !hasMixedTypes && (() => {
                                const isMultipolar = sortedCables[0]?.type === 'multipolar' || (sortedCables[0]?.poli && Array.isArray(sortedCables[0].poli));
                                return isMultipolar ? (
                                    <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {t('schematic.multipolarCable')}
                                    </div>
                                ) : (
                                    <div className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {t('schematic.singleCable')}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <div className="mb-6 bg-gray-50 rounded-lg p-4">
                    <HybridCableVisualization
                        cables={sortedCables}
                        settings={settings}
                        siglatura={siglatura}
                        hoveredCable={localHoveredCable}
                        setHoveredCable={setLocalHoveredCable}
                        isMobile={isMobile}
                        availableSheaths={availableSheaths}
                    />
                </div>

                {!isMobile && (
                    <div className="bg-blue-50 rounded-lg p-6 h-full flex flex-col">
                        <div className="space-y-2">
                            {sortedCables.map((cable, idx) => {
                                const isHovered = localHoveredCable === cable.originalIndex;
                                const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);

                                return (
                                    <div
                                        key={`${cable.originalIndex}-${idx}`}
                                        className="transition-all duration-200"
                                        onMouseEnter={() => setLocalHoveredCable(cable.originalIndex)}
                                        onMouseLeave={() => setLocalHoveredCable(null)}
                                        style={isHovered ? {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 8px rgba(59, 130, 246, 0.3)'
                                        } : {}}
                                    >
                                        <CableTessera
                                            cable={cable}
                                            displaySiglatura={displaySiglatura}
                                            onCableClick={null}
                                            onEditCable={editCable}
                                            onEditMultipolarCable={editMultipolarCable}
                                            onDeleteCable={deleteCable}
                                            onDeleteMultipolarCable={deleteMultipolarCable}
                                            showSiglatura={true}
                                            clickable={false}
                                            settings={settings}
                                            validateCable={validateCable}
                                            isHovered={isHovered}
                                            editingIndex={editingIndex}
                                            editingMultipolarIndex={editingMultipolarIndex}
                                            showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
                                            onToggleDelete={() => toggleDeleteButton(cable.originalIndex)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Funzione per ottenere il titolo del gruppo con sottogruppo
    const getGroupDisplayTitleWithSubgroup = (groupData, settings) => {
        const baseTitle = getGroupDisplayTitle(groupData.groupName, settings.groupTitles);

        if (dynamicGroupsDivision && groupData.subgroupIndex && groupData.totalSubgroups) {
            return `${baseTitle} (${groupData.subgroupIndex}/${groupData.totalSubgroups})`;
        }

        return baseTitle;
    };

    // Funzione per ottenere il titolo del gruppo normale con sottogruppo
    const getDisplayTitleWithSubgroup = (siglatura, sortedCables, isGrouppoForzato, nomeGruppo, settings) => {
        // Gestione speciale per gruppi guaina
        if (siglatura && siglatura.startsWith('sheath_')) {
            const sheathId = parseInt(siglatura.replace('sheath_', ''));
            const sheath = availableSheaths.find(s => s.id === sheathId);
            const baseTitle = sheath ? (sheath.fullLabel || `${t('schematic.sheathLabel')} ${sheath.label}`) : t('schematic.sheathNumber', { id: sheathId });

            if (dynamicGroupsDivision && sortedCables.length > 0 && sortedCables[0].subgroupIndex && sortedCables[0].totalSubgroups) {
                return `${baseTitle} (${sortedCables[0].subgroupIndex}/${sortedCables[0].totalSubgroups})`;
            }

            return baseTitle;
        }

        const baseTitle = getDisplayTitle(siglatura, sortedCables, isGrouppoForzato, nomeGruppo, settings);

        if (dynamicGroupsDivision && sortedCables.length > 0 && sortedCables[0].subgroupIndex && sortedCables[0].totalSubgroups) {
            return `${baseTitle} (${sortedCables[0].subgroupIndex}/${sortedCables[0].totalSubgroups})`;
        }

        return baseTitle;
    };

    // Wrapper che usa il modulo centralizzato per calcolare l'altezza di un gruppo
    function calculateGroupHeight(cables, settings, usableWidth = PDF_CONSTANTS.USABLE_WIDTH, groupIdentifier = null) {
        return calculateGroupHeightFromDOM(cables, settings, usableWidth, groupIdentifier);
    }

    // Funzione per dividere i gruppi in sottogruppi basati sullo spazio disponibile
    // ✅ REFACTORED: Usa il modulo centralizzato pdfHeightEstimation per i calcoli
    function divideGroupsIntoSubgroups(groupedCables) {
        if (!dynamicGroupsDivision) return groupedCables;

        const dividedGroups = {};
        // ✅ FIX: La prima pagina è riservata ai riepiloghi, i gruppi iniziano dalla seconda
        const maxHeight = PDF_CONSTANTS.MAX_GROUP_HEIGHT_OTHER_PAGES;
        const scale = getScale();

        // Funzione helper condivisa per dividere sotto-gruppi logici in pagine
        const splitLogicalSubGroupsIntoPages = (logicalSubGroups, siglatura) => {
            // Rileva se è un gruppo guaina (i cavi singoli condividono un SVG)
            const isSheathGroup = siglatura && siglatura.startsWith('sheath_');

            // Calcola altezza totale usando il modulo centralizzato
            // Per guaine: usa la stima corretta che "raggruppa" i cavi singoli in un unico SVG
            const totalGroupHeight = estimateGroupHeightFromLogicalSubGroups(logicalSubGroups, scale, isSheathGroup);

            // Se il gruppo entra in una pagina, non dividerlo
            if (totalGroupHeight <= maxHeight) {
                return null; // Segnala che non serve divisione
            }

            // ✅ Per gruppi guaina: divisione speciale basata su chunk di cavi singoli
            if (isSheathGroup) {
                // Separa cavi singoli standard da altri tipi
                const singleCableGroups = [];
                const otherGroups = [];
                logicalSubGroups.forEach(sg => {
                    if (sg.isSingle && !(sg.cables[0].poli && Array.isArray(sg.cables[0].poli))) {
                        singleCableGroups.push(sg);
                    } else {
                        otherGroups.push(sg);
                    }
                });

                // Calcola quanti cavi singoli entrano per pagina
                let maxSinglesPerChunk = singleCableGroups.length;
                for (let n = singleCableGroups.length; n >= 1; n--) {
                    if (estimateSheathChunkHeight(n, scale) <= maxHeight) {
                        maxSinglesPerChunk = n;
                        break;
                    }
                }

                // Dividi i cavi singoli in chunk
                const finalSubGroups = [];
                let subgroupIndex = 1;

                for (let i = 0; i < singleCableGroups.length; i += maxSinglesPerChunk) {
                    const chunk = singleCableGroups.slice(i, i + maxSinglesPerChunk);
                    finalSubGroups.push({
                        cables: chunk.flatMap(sg => sg.cables),
                        subgroupIndex: subgroupIndex++,
                        totalSubgroups: 0
                    });
                }

                // Aggiungi i gruppi non-singoli (multipolari, multipli) come ultimo chunk
                if (otherGroups.length > 0) {
                    const lastGroup = finalSubGroups[finalSubGroups.length - 1];
                    const otherCables = otherGroups.flatMap(sg => sg.cables);
                    // Prova ad aggiungere gli altri al ultimo chunk
                    const lastChunkWithOthers = estimateSheathChunkHeight(lastGroup.cables.length, scale) +
                        otherGroups.reduce((sum, sg) => sum + estimateLogicalSubGroupHeight(sg, scale), 0);
                    if (lastChunkWithOthers <= maxHeight) {
                        lastGroup.cables = [...lastGroup.cables, ...otherCables];
                    } else {
                        finalSubGroups.push({
                            cables: otherCables,
                            subgroupIndex: subgroupIndex++,
                            totalSubgroups: 0
                        });
                    }
                }

                finalSubGroups.forEach(sg => { sg.totalSubgroups = finalSubGroups.length; });
                return finalSubGroups.length > 1 ? finalSubGroups : null;
            }

            // Il gruppo è troppo alto, dividilo per SOTTOGRUPPI LOGICI
            const finalSubGroups = [];
            let currentSubGroup = [];
            // ✅ FIX: Ogni sotto-gruppo avrà il suo header + tabella dettagli
            let currentSubGroupHeight = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;
            let currentSubGroupIndex = 1;

            for (let i = 0; i < logicalSubGroups.length; i++) {
                const logicalSubGroup = logicalSubGroups[i];
                // ✅ Usa il calcolo centralizzato invece delle formule hardcoded
                const subGroupHeight = estimateLogicalSubGroupHeight(logicalSubGroup, scale);

                // Se aggiungere questo sottogruppo logico supera il limite
                if (currentSubGroupHeight + subGroupHeight > maxHeight && currentSubGroup.length > 0) {
                    finalSubGroups.push({
                        cables: currentSubGroup.flat(),
                        subgroupIndex: currentSubGroupIndex,
                        totalSubgroups: 0
                    });

                    currentSubGroup = [];
                    currentSubGroupHeight = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;
                    currentSubGroupIndex++;
                }

                currentSubGroup.push(logicalSubGroup.cables);
                currentSubGroupHeight += subGroupHeight;
            }

            // Aggiungi l'ultimo sottogruppo
            if (currentSubGroup.length > 0) {
                finalSubGroups.push({
                    cables: currentSubGroup.flat(),
                    subgroupIndex: currentSubGroupIndex,
                    totalSubgroups: 0
                });
            }

            // Imposta il numero totale di sottogruppi
            finalSubGroups.forEach(subGroup => {
                subGroup.totalSubgroups = finalSubGroups.length;
            });

            return finalSubGroups;
        };

        Object.entries(groupedCables).forEach(([siglatura, groupData]) => {
            if (groupData.type === 'forced_group') {
                // Per gruppi forzati
                const allCables = groupData.allCables;
                const logicalSubGroups = analyzeAndGroupCables(allCables, settings);

                const finalSubGroups = splitLogicalSubGroupsIntoPages(logicalSubGroups, siglatura);

                if (!finalSubGroups) {
                    // Il gruppo entra in una pagina
                    dividedGroups[siglatura] = groupData;
                    return;
                }

                // Crea i gruppi divisi
                finalSubGroups.forEach(subGroup => {
                    const subgroupKey = `${siglatura}_${subGroup.subgroupIndex}`;
                    dividedGroups[subgroupKey] = {
                        ...groupData,
                        allCables: subGroup.cables,
                        groupName: groupData.groupName,
                        originalGroupName: groupData.groupName,
                        subgroupIndex: subGroup.subgroupIndex,
                        totalSubgroups: subGroup.totalSubgroups
                    };
                });
            } else {
                // Per gruppi normali
                const groupCables = Array.isArray(groupData) ? groupData : [];
                const logicalSubGroups = analyzeAndGroupCables(groupCables, settings);

                const finalSubGroups = splitLogicalSubGroupsIntoPages(logicalSubGroups, siglatura);

                if (!finalSubGroups) {
                    // Il gruppo entra in una pagina
                    dividedGroups[siglatura] = groupData;
                    return;
                }

                // Crea i gruppi divisi
                finalSubGroups.forEach(subGroup => {
                    const subgroupKey = `${siglatura}_${subGroup.subgroupIndex}`;
                    dividedGroups[subgroupKey] = subGroup.cables.map(cable => ({
                        ...cable,
                        originalSiglatura: siglatura,
                        subgroupIndex: subGroup.subgroupIndex,
                        totalSubgroups: subGroup.totalSubgroups
                    }));
                });
            }
        });

        return dividedGroups;
    }

    const finalGroupsToRender = useMemo(() => {
        const sortedOriginalGroups = Object.entries(originalGroupedCables || {})
            .sort((a, b) => {
                const getCablesArray = (groupData) => {
                    if (Array.isArray(groupData)) {
                        return groupData;
                    } else if (groupData.type === 'forced_group') {
                        return groupData.allCables;
                    } else {
                        return [];
                    }
                };

                const cablesA = getCablesArray(a[1]);
                const cablesB = getCablesArray(b[1]);

                const getFirstIndex = (cables) => {
                    return cables.length > 0 ? Math.min(...cables.map(c => c.originalIndex)) : 0;
                };

                if (settings.listSortMode === 'numerazione') {
                    return getFirstIndex(cablesA) - getFirstIndex(cablesB);
                }

                // Per siglatura, colore, sezione, coloreSezione: multipolari sempre in fondo
                const aIsMultipolar = cablesA[0]?.poli && Array.isArray(cablesA[0].poli);
                const bIsMultipolar = cablesB[0]?.poli && Array.isArray(cablesB[0].poli);
                if (aIsMultipolar && !bIsMultipolar) return 1;
                if (!aIsMultipolar && bIsMultipolar) return -1;

                if (settings.listSortMode === 'siglatura') {
                    // Ordina per siglatura con sort naturale
                    const sigA = cablesA[0]?.siglatura || '';
                    const sigB = cablesB[0]?.siglatura || '';
                    const numA = parseInt(sigA);
                    const numB = parseInt(sigB);
                    if (!isNaN(numA) && !isNaN(numB)) {
                        return numA - numB;
                    }
                    return String(sigA).localeCompare(String(sigB));
                }

                // Per colore, sezione, coloreSezione: usa universalSort
                const context = createSortContext(settings.lingua);
                const groupA = { cables: cablesA };
                const groupB = { cables: cablesB };
                const [sortedA] = universalSort([groupA, groupB], settings.listSortMode, context);
                return sortedA === groupA ? -1 : 1;
            });

        const finalGroups = [];

        sortedOriginalGroups.forEach(([siglatura, groupData]) => {
            if (!dynamicGroupsDivision) {
                finalGroups.push([siglatura, groupData]);
                return;
            }

            const singleGroupObject = { [siglatura]: groupData };
            const dividedGroup = divideGroupsIntoSubgroups(singleGroupObject);

            Object.entries(dividedGroup)
                .sort((a, b) => {
                    if (a[0].includes('_') && b[0].includes('_')) {
                        const [groupA, subA] = a[0].split('_');
                        const [groupB, subB] = b[0].split('_');

                        if (groupA === groupB) {
                            return parseInt(subA, 10) - parseInt(subB, 10);
                        }
                    }
                    return 0;
                })
                .forEach(entry => finalGroups.push(entry));
        });

        return finalGroups;
    }, [originalGroupedCables, dynamicGroupsDivision, settings, divideGroupsIntoSubgroups]);

    return (
        <>
            {!isPreviewMode && (
                <div className="mb-4 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 xl:gap-0">
                    <div className="flex items-center gap-4">
                        <label className="font-medium">{t('schematic.sortBy')}</label>
                        <select
                            value={settings.listSortMode || 'numerazione'}
                            onChange={e => setListSortMode(e.target.value)}
                            className="border rounded px-2 py-1"
                        >
                            {sortOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-4 items-center">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="dynamicGroupsDivision"
                                checked={dynamicGroupsDivision}
                                onChange={(e) => setDynamicGroupsDivision(e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="dynamicGroupsDivision" className="text-sm font-medium text-gray-700">
                                Divisione Dinamica Gruppi
                            </label>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="nestedSchematicView"
                                checked={useNestedSchematic}
                                onChange={(e) => setUseNestedSchematic(e.target.checked)}
                                className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <label htmlFor="nestedSchematicView" className="text-sm font-medium text-gray-700">
                                {t('schematic.nestedViewToggle')}
                            </label>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="debugMode"
                                checked={debugMode}
                                onChange={(e) => setDebugMode(e.target.checked)}
                                className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                            />
                            <label htmlFor="debugMode" className="text-sm font-medium text-gray-700">
                                Debug Mode
                            </label>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    if (cablesForGrouping.length === 0) {
                                        alert(t('schematic.noCablesSelected'));
                                        return;
                                    }
                                    const exportFn = useNestedSchematic ? exportSchematicNestedToPDF : exportSchematicToPDFColor;
                                    exportFn(cablesForGrouping, { ...settings, lingua: language }, () => { }, () => { }, availableSheaths, setDynamicGroupsDivision, debugMode);
                                }}
                                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2"
                            >
                                <Icons.Download className="w-4 h-4" />
                                {t('schematic.pdfColors')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div id="schematic-container" className={isPreviewMode ? "" : "space-y-8"}>
                {useNestedSchematic ? (
                    <SchematicNestedVisualization
                        nodes={nestedSchematicNodes}
                        settings={settings}
                        availableSheaths={availableSheaths}
                        isMobile={isMobile}
                        localHoveredCable={localHoveredCable}
                        setLocalHoveredCable={setLocalHoveredCable}
                        editCable={editCable}
                        editMultipolarCable={editMultipolarCable}
                        deleteCable={deleteCable}
                        deleteMultipolarCable={deleteMultipolarCable}
                        validateCable={validateCable}
                        editingIndex={editingIndex}
                        editingMultipolarIndex={editingMultipolarIndex}
                        showDeleteButtons={showDeleteButtons}
                        toggleDeleteButton={toggleDeleteButton}
                        isSelectionModeEnabled={isSelectionModeEnabled}
                        selectedCables={selectedCables}
                    />
                ) : isPreviewMode ? (
                    <div className="relative group">
                        {/* Header personalizzato per previewMode con frecce in alto a destra */}
                        {previewTitle && (
                            <div className="flex justify-between items-center mb-6">
                                {previewTitle}
                                {finalGroupsToRender.length > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPreviewIndex(prev => prev > 0 ? prev - 1 : finalGroupsToRender.length - 1)}
                                            className="p-1.5 bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 rounded-md shadow-sm transition-all"
                                            aria-label="Precedente"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                                        </button>
                                        <button
                                            onClick={() => setCurrentPreviewIndex(prev => prev < finalGroupsToRender.length - 1 ? prev + 1 : 0)}
                                            className="p-1.5 bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 rounded-md shadow-sm transition-all"
                                            aria-label="Successivo"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="overflow-visible bg-transparent pb-8">
                            {finalGroupsToRender.length > 0 && (
                                <div className="transition-all duration-300">
                                    {renderLegacyGroup(finalGroupsToRender[currentPreviewIndex] || finalGroupsToRender[0])}
                                </div>
                            )}
                        </div>

                        {/* Pallini (Dots) */}
                        {finalGroupsToRender.length > 1 && (
                            <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2">
                                {finalGroupsToRender.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentPreviewIndex(idx)}
                                        className={`h-2 rounded-full transition-all ${idx === currentPreviewIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2 hover:bg-gray-400'}`}
                                        aria-label={`Vai alla slide ${idx + 1}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-8">
                        {finalGroupsToRender.map(renderLegacyGroup)}
                    </div>
                )}
            </div>
        </>
    );
};

export default SchematicCableView;